/**
 * Application state.
 *
 * Signals hold what the screens read; every mutation writes to IndexedDB
 * first and then refreshes the signal, so the database is always the truth and
 * a reload never shows something that was not persisted.
 */
import { signal } from '@preact/signals';
import { byDayRange, byIndex, del, get, getAll, metaGet, metaSet, put, update } from './data/db';
import { readLegacyLocalStorage } from './data/legacy';
import { loadSettings, saveSettings } from './data/export';
import { mergeDay } from './data/biometrics';
import { DEFAULT_SETTINGS, type CheckIn, type Compound, type DailyMetrics, type DoseLog, type Exercise, type FoodItem, type FoodLog, type Protocol, type Reading, type Routine, type Run, type Settings, type Vial, type WorkoutSession } from './data/schema';
import { addDays, lastNDays, nowIso, toDay } from './lib/time';
import { newId } from './lib/id';
import type { CoreFood } from './domain/foodsearch';

export const today = signal<string>(toDay());
export const settings = signal<Settings>(DEFAULT_SETTINGS);
export const ready = signal(false);

export const metricsToday = signal<DailyMetrics | undefined>(undefined);
export const metricsRange = signal<DailyMetrics[]>([]);
export const checkinToday = signal<CheckIn | undefined>(undefined);
export const checkinsRange = signal<CheckIn[]>([]);

export const mealsToday = signal<FoodLog[]>([]);
export const compounds = signal<Compound[]>([]);
export const vials = signal<Vial[]>([]);
export const protocols = signal<Protocol[]>([]);
export const recentDoses = signal<DoseLog[]>([]);
export const exercises = signal<Exercise[]>([]);
export const routines = signal<Routine[]>([]);
export const sessionsRecent = signal<WorkoutSession[]>([]);
export const runsRecent = signal<Run[]>([]);

export const toast = signal<{ text: string; error?: boolean } | null>(null);
/** The previous app's data was found in this origin's localStorage and not yet imported. */
export const legacyPending = signal(false);

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function notify(text: string, error = false): void {
  toast.value = { text, error };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 2600);
}

// --------------------------------------------------------------------------
// The bundled food database, loaded lazily on first use.
// --------------------------------------------------------------------------

export const coreFoods = signal<CoreFood[] | null>(null);
export const coreFoodsLoading = signal(false);
let coreFoodsPromise: Promise<CoreFood[]> | null = null;

export function loadCoreFoods(): Promise<CoreFood[]> {
  if (coreFoods.value) return Promise.resolve(coreFoods.value);
  if (coreFoodsPromise) return coreFoodsPromise;
  coreFoodsLoading.value = true;
  coreFoodsPromise = fetch('/data/foods-core.json')
    .then((r) => {
      if (!r.ok) throw new Error(`food database unavailable (${r.status})`);
      return r.json();
    })
    .then((d: { foods: CoreFood[] }) => {
      coreFoods.value = d.foods;
      return d.foods;
    })
    .catch((err) => {
      notify('Food database unavailable offline until first load.', true);
      throw err;
    })
    .finally(() => { coreFoodsLoading.value = false; });
  return coreFoodsPromise;
}

// --------------------------------------------------------------------------
// Refresh
// --------------------------------------------------------------------------

export async function refreshAll(): Promise<void> {
  const day = today.value;
  // Six months: enough to see a before/after window around a compound that
  // started a season ago.
  const from = addDays(day, -182);
  const [s, m, range, c, cRange, meals, comp, v, p, doses, ex, rt, sess, runs] = await Promise.all([
    loadSettings(),
    get('metrics', day),
    byDayRange('metrics', from, day),
    get('checkins', day),
    byDayRange('checkins', from, day),
    byDayRange('foodLogs', day, day),
    getAll('compounds'),
    getAll('vials'),
    getAll('protocols'),
    byDayRange('doses', from, day),
    getAll('exercises'),
    getAll('routines'),
    byDayRange('sessions', from, day),
    byDayRange('runs', addDays(day, -60), day),
  ]);
  settings.value = s;
  metricsToday.value = m;
  metricsRange.value = range.sort((a, b) => a.day.localeCompare(b.day));
  checkinToday.value = c;
  checkinsRange.value = cRange.sort((a, b) => a.day.localeCompare(b.day));
  mealsToday.value = meals.sort((a, b) => a.at.localeCompare(b.at));
  compounds.value = comp.filter((x) => x.active !== false);
  vials.value = v;
  protocols.value = p.filter((x) => x.active);
  recentDoses.value = doses.sort((a, b) => b.at.localeCompare(a.at));
  exercises.value = ex;
  routines.value = rt.filter((x) => !x.archivedAt);
  sessionsRecent.value = sess.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  runsRecent.value = runs.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

export async function init(): Promise<void> {
  today.value = toDay();
  await seedIfEmpty();
  await refreshAll();
  await adoptProfileStart();
  legacyPending.value = !(await metaGet<boolean>('legacyMigrated')) && readLegacyLocalStorage() !== null;
  ready.value = true;
}

/**
 * A start date carried in the profile (from the previous app) lands on the
 * matching protocol when that protocol has none. Runs on every launch so a
 * restored backup takes effect without a second step.
 */
async function adoptProfileStart(): Promise<void> {
  const startedOn = settings.value.profile?.startedOn;
  if (!startedOn) return;
  let changed = false;
  for (const p of protocols.value) {
    if (p.compoundId === 'reta' && !p.startedOn) { await put('protocols', { ...p, startedOn }); changed = true; }
  }
  if (changed) await refreshAll();
}

export async function markLegacyMigrated(): Promise<void> {
  await metaSet('legacyMigrated', true);
  legacyPending.value = false;
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  const next = { ...settings.value, ...patch, updatedAt: nowIso() };
  await saveSettings(next);
  settings.value = next;
}

/** Record a value the user typed. Manual entries outrank every sync. */
export async function setMetric(field: keyof DailyMetrics, value: number, day = today.value): Promise<void> {
  const reading: Reading = { value, source: 'manual', at: nowIso() };
  await update('metrics', day, (cur) => mergeDay(cur, day, { [field]: reading }));
  await refreshAll();
}

/** Merge readings arriving from a sync, which never displace manual entries. */
export async function mergeMetrics(days: Map<string, Record<string, Reading>>): Promise<number> {
  let touched = 0;
  for (const [day, fields] of days) {
    await update('metrics', day, (cur) => mergeDay(cur, day, fields));
    touched++;
  }
  return touched;
}

export async function saveCheckIn(patch: Partial<CheckIn>): Promise<void> {
  const day = today.value;
  await update('checkins', day, (cur) => ({ ...(cur ?? { day }), ...patch, day, updatedAt: nowIso() }));
  checkinToday.value = await get('checkins', day);
}

export async function addMeal(log: Omit<FoodLog, 'id'>): Promise<void> {
  await put('foodLogs', { ...log, id: newId() });
  // Frequency drives the one-tap chips, so count every logged item.
  for (const item of log.items) {
    if (!item.foodId) continue;
    await update('foods', item.foodId, (cur) =>
      cur ? { ...cur, useCount: (cur.useCount ?? 0) + 1, lastUsedAt: nowIso() } : cur);
  }
  await refreshAll();
}

export async function deleteMeal(id: string): Promise<void> {
  await del('foodLogs', id);
  await refreshAll();
}

export async function saveFood(food: FoodItem): Promise<void> {
  await put('foods', food);
}

export async function frequentFoods(limit = 8): Promise<FoodItem[]> {
  const all = await getAll('foods');
  return all
    .filter((f) => (f.useCount ?? 0) > 0)
    .sort((a, b) => (b.useCount ?? 0) - (a.useCount ?? 0))
    .slice(0, limit);
}

export async function addDose(dose: Omit<DoseLog, 'id'>): Promise<void> {
  await put('doses', { ...dose, id: newId() });
  await refreshAll();
}

export async function deleteDose(id: string): Promise<void> {
  await del('doses', id);
  await refreshAll();
}

export async function saveCompound(c: Compound): Promise<void> {
  await put('compounds', c);
  await refreshAll();
}

export async function saveVial(v: Vial): Promise<void> {
  await put('vials', v);
  await refreshAll();
}

export async function saveProtocol(p: Protocol): Promise<void> {
  await put('protocols', p);
  await refreshAll();
}

export async function saveRoutine(r: Routine): Promise<void> {
  await put('routines', r);
  await refreshAll();
}

export async function saveExercise(e: Exercise): Promise<void> {
  await put('exercises', e);
  await refreshAll();
}

export async function saveSession(s: WorkoutSession): Promise<void> {
  await put('sessions', s);
  await refreshAll();
}

export async function saveRun(r: Run): Promise<void> {
  await put('runs', r);
  await refreshAll();
}

/** Doses already drawn from a vial, as mass. Drives "doses left". */
export async function withdrawnFromVial(vialId: string): Promise<number> {
  const doses = await byIndex('doses', 'by_vial', vialId);
  return doses.reduce((sum, d) => {
    if (d.skipped) return sum;
    const prime = d.primeLossMl ?? 0;
    return sum + d.doseMg + (prime > 0 ? prime * primeConcentration(vialId) : 0);
  }, 0);
}

/** Concentration lookup used only for converting prime loss to mass. */
function primeConcentration(vialId: string): number {
  const vial = vials.value.find((v) => v.id === vialId);
  if (!vial || !vial.diluentMl) return 0;
  return vial.contentMg / vial.diluentMl;
}

export function last30Days(): string[] {
  return lastNDays(30, today.value);
}

// --------------------------------------------------------------------------
// First-run seed
// --------------------------------------------------------------------------

/**
 * Seed the protocol the user described, once, so the first launch is useful
 * rather than empty. Everything seeded is ordinary editable data; nothing here
 * is a recommendation, it is the setup the user stated.
 */
async function seedIfEmpty(): Promise<void> {
  const existing = await getAll('compounds');
  if (existing.length) return;

  const at = nowIso();
  const reta: Compound = {
    id: 'reta', name: 'Retatrutide', route: 'subcutaneous', reconstituted: true,
    displayUnit: 'mg', color: '#e98862', active: true, createdAt: at,
  };
  const bpc: Compound = {
    id: 'bpc157-oral', name: 'BPC-157 (oral)', route: 'oral', reconstituted: false,
    displayUnit: 'mcg', color: '#8fa67f', active: true, createdAt: at,
  };
  await put('compounds', reta);
  await put('compounds', bpc);

  await put('vials', {
    id: newId(), compoundId: 'reta', label: 'Vial 1',
    contentMg: 10, diluentMl: 2, reconstitutedAt: at, openedAt: at,
    storage: 'fridge', status: 'active', createdAt: at,
  });

  await put('protocols', {
    id: newId(), compoundId: 'reta', doseMg: 1.5, kind: 'weekly',
    weekdays: [1], startDay: today.value, active: true, createdAt: at,
    sites: ['abdomen-L', 'abdomen-R', 'thigh-L', 'thigh-R'],
  });
}
